/**
 * A status option stores one hue. A chip needs two colours, and painting the
 * stored hue as the text was the bug: an amber or lime status printed its own
 * hue on a tint of itself, which is unreadable. So the pair is derived instead
 * - the hue laid thinly over white for the fill, and the same hue darkened for
 * the text until the two provably clear WCAG AA.
 */

/** Share of the hue laid over white for the fill. */
const BACKGROUND_TINT = 0.14;
/** Lightness the text starts at, before it is darkened to reach contrast. */
const TEXT_START_LIGHTNESS = 0.42;
/** Saturation ceiling: a fully saturated dark text reads as a different colour. */
const TEXT_MAX_SATURATION = 0.7;
const CONTRAST_TARGET = 4.5;
const DARKEN_STEP = 0.01;
/** Floor for the search. Past this the text reads as black rather than as its hue. */
const TEXT_MIN_LIGHTNESS = 0.1;

export interface StatusChipColors {
  backgroundColor: string;
  color: string;
}

/** neutral-100 on neutral-500: the `bg-muted` / `text-muted-foreground` pair. */
const NEUTRAL_FALLBACK: StatusChipColors = { backgroundColor: '#f5f5f5', color: '#737373' };

type Rgb = [number, number, number];

function parseHex(hex: string): Rgb | null {
  let raw = hex.trim();
  if (raw.startsWith('#')) raw = raw.slice(1);
  if (raw.length === 3) {
    raw = raw
      .split('')
      .map((c) => c + c)
      .join('');
  }
  if (!/^[0-9a-f]{6}$/i.test(raw)) return null;
  return [
    parseInt(raw.slice(0, 2), 16),
    parseInt(raw.slice(2, 4), 16),
    parseInt(raw.slice(4, 6), 16),
  ];
}

function round([r, g, b]: Rgb): Rgb {
  return [Math.round(r), Math.round(g), Math.round(b)];
}

function toHex([r, g, b]: Rgb): string {
  return `#${[r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('')}`;
}

function rgbToHsl([r, g, b]: Rgb): [number, number, number] {
  const [rn, gn, bn] = [r / 255, g / 255, b / 255];
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  const delta = max - min;
  if (delta === 0) return [0, 0, l];

  const s = delta / (1 - Math.abs(2 * l - 1));
  let h: number;
  if (max === rn) h = ((gn - bn) / delta) % 6;
  else if (max === gn) h = (bn - rn) / delta + 2;
  else h = (rn - gn) / delta + 4;
  return [(((h * 60) % 360) + 360) % 360, s, l];
}

function hslToRgb(h: number, s: number, l: number): Rgb {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  const [r, g, b] =
    h < 60
      ? [c, x, 0]
      : h < 120
        ? [x, c, 0]
        : h < 180
          ? [0, c, x]
          : h < 240
            ? [0, x, c]
            : h < 300
              ? [x, 0, c]
              : [c, 0, x];
  return [(r + m) * 255, (g + m) * 255, (b + m) * 255];
}

function relativeLuminance([r, g, b]: Rgb): number {
  const [rl, gl, bl] = [r, g, b].map((channel) => {
    const v = channel / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * rl + 0.7152 * gl + 0.0722 * bl;
}

function contrastRatio(a: Rgb, b: Rgb): number {
  const [light, dark] = [relativeLuminance(a), relativeLuminance(b)].sort((x, y) => y - x);
  return (light + 0.05) / (dark + 0.05);
}

/**
 * The fill + text pair for a status chip of the given hue. An unparseable hue
 * falls back to the neutral pair rather than throwing: the colour is
 * user-supplied and reaches here straight from a persisted layout.
 */
export function statusChipColors(hex: string): StatusChipColors {
  const rgb = parseHex(hex);
  if (!rgb) return NEUTRAL_FALLBACK;

  const background = rgb.map((channel) => 255 - (255 - channel) * BACKGROUND_TINT) as Rgb;
  const [h, s] = rgbToHsl(rgb);
  const saturation = Math.min(s, TEXT_MAX_SATURATION);

  // HSL lightness is not perceptual: a yellow and a blue at the same lightness
  // are nowhere near the same brightness, so one fixed value leaves the warm
  // half of the wheel short of AA. Darkening until the pair actually measures
  // makes the guarantee hold for any hue a user picks, not just the seeded
  // ones. Both sides are rounded first, so what is measured is the pair of hex
  // strings that ships rather than the floats behind them.
  const fill = round(background);
  let lightness = TEXT_START_LIGHTNESS;
  let text = round(hslToRgb(h, saturation, lightness));
  while (lightness > TEXT_MIN_LIGHTNESS && contrastRatio(fill, text) < CONTRAST_TARGET) {
    lightness -= DARKEN_STEP;
    text = round(hslToRgb(h, saturation, lightness));
  }

  return { backgroundColor: toHex(fill), color: toHex(text) };
}
